/**
 * Config Poller + Version Guard
 *
 * Periodically re-fetches all UI config from the server, keeping the Redux
 * store (and every consumer of uiConfigurations) always up to date.
 *
 * Version strategy — localStorage-based (no build-time env variable):
 *   "nine_ui_version" holds the last confirmed server version.
 *
 *   • First visit (no stored version) → silently record serverVersion, no reload.
 *     The user is already on the latest build; we just don't have a record yet.
 *
 *   • Version mismatch → silently and automatically force a hard refresh to 
 *     get the latest bundle without requiring user interaction.
 *
 * Loop prevention — two layers:
 *   1. Write serverVersion to localStorage BEFORE reloading so the next run
 *      sees a match immediately (covers 99.9 % of browsers).
 *   2. sessionStorage cooldown ("nine_cfg_reload_ts") as a safety net for the
 *      edge case where localStorage is wiped despite the reload. sessionStorage
 *      survives window.location.reload() within the same tab.
 */

import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../dataManagement/uiDb/UiDb";
import { fetchUiConfigurations } from "../../../services/thunks/AdminThunk";
import AppConfig from "../../../config/config";

const RELOAD_COOLDOWN_MS = AppConfig.polling.reloadCooldownMs;

const VERSION_STORAGE_KEY = "nine_ui_version";
const RELOAD_GUARD_KEY    = "nine_cfg_reload_ts";
const DAY_MS = 24 * 60 * 60 * 1000;

/** Milliseconds from now until the next 4:00 AM IST (UTC+05:30 → 22:30 UTC). */
function msUntilNext4amIST(): number {
  const now = Date.now();
  const next = new Date();
  next.setUTCHours(22, 30, 0, 0); // 22:30 UTC = 04:00 IST
  if (next.getTime() <= now) next.setUTCDate(next.getUTCDate() + 1);
  return next.getTime() - now;
}

const VersionCheckPopup: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();

  const serverVersion: string =
    useSelector(
      (state: RootState) =>
        state.general.uiConfigurations?.UIVersion?.configValue
    ) || "";

  const handleRefresh = useCallback(async (version: string) => {
    // Layer 1: write new version before reloading → no mismatch on next run
    localStorage.setItem(VERSION_STORAGE_KEY, version);
    // Layer 2: stamp cooldown in sessionStorage as a loop-prevention safety net
    sessionStorage.setItem(RELOAD_GUARD_KEY, Date.now().toString());

    // PWA cache + service worker hold onto old index.html → reload alone
    // ships the stale bundle. Wipe both before reloading so the next request
    // actually hits the network.
    try {
      if ("caches" in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      // Best-effort — fall through to a plain reload if cache clearing fails
    }

    // Cache-busting query bypasses any leftover HTTP cache for index.html
    const url = new URL(window.location.href);
    url.searchParams.set("v", Date.now().toString());
    window.location.replace(url.toString());
  }, []);

  const handleVersionCheck = useCallback(() => {
    if (!serverVersion) return;

    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

    if (!storedVersion) {
      // First visit — no previous version on record.
      // Silently store the current server version and continue.
      localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
      return;
    }

    if (storedVersion === serverVersion) return; // Already up to date

    // True mismatch — a new version was deployed. Refresh automatically.
    handleRefresh(serverVersion);
  }, [serverVersion, handleRefresh]);

  // Run the check every time a poll brings a fresh UIVersion into Redux
  useEffect(() => {
    handleVersionCheck();
  }, [serverVersion, handleVersionCheck]);

  // Re-fetch config once a day at 4:00 AM IST — keeps ALL config consumers
  // (including version check) current without hammering the server.
  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const timeoutId = setTimeout(() => {
      dispatch(fetchUiConfigurations());
      intervalId = setInterval(() => {
        dispatch(fetchUiConfigurations());
      }, DAY_MS);
    }, msUntilNext4amIST());

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, [dispatch]);

  // No UI required, strictly a background behavioral component
  return null;
};

export default VersionCheckPopup;
